import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Building2, Search, SlidersHorizontal, Loader2,
  CheckCircle2, XCircle, AlertTriangle, Clock, ArrowUpDown,
  Trash2, MoreHorizontal, Eye, RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { Json } from "@/integrations/supabase/types";
import { verdictShort, verdictLong } from "@/lib/verdict-display";
import { isSuperadminWorkspace } from "@/lib/superadmin-workspace";

interface DealRow {
  id: string;
  name: string;
  context: Json;
  strategy: string;
  status: string;
  deal_type_id: string | null;
  preliminary_result: Json | null;
  final_result: Json | null;
  created_at: string;
  updated_at: string;
}

interface IntelRow {
  deal_id: string;
  orchestrator_output: Json | null;
  final_decision: Json | null;
  confidence_engine: Json | null;
  created_at?: string;
  updated_at?: string;
}

interface VersionRow {
  deal_id: string;
  deal_score: number | null;
  verdict: string | null;
}

type Verdict = "GO" | "MAYBE" | "NO-GO" | undefined;

const asObj = (v: unknown): Record<string, any> | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Record<string, any>) : null;

const normalizeVerdict = (raw: unknown): Verdict => {
  if (!raw) return undefined;
  const s = String(raw).toUpperCase().replace(/[\s-]+/g, "_");
  if (s === "GO" || s === "STRONG_GO" || s === "PROCEED" || s === "PROCEED_WITH_FOCUS") return "GO";
  if (s === "MAYBE" || s === "CAUTION" || s === "PROCEED_WITH_CAUTION") return "MAYBE";
  if (s === "NO" || s === "NO_GO" || s === "PASS" || s === "PASS_ON_DEAL" || s === "REJECT") return "NO-GO";
  return undefined;
};

const getDealMilestone = (deal: DealRow, intel?: IntelRow, version?: VersionRow) => {
  const { verdict, score } = extractDealResult(deal, intel, version);
  if (verdict === "GO") return { label: "Top Opportunity", className: "text-green-500" };
  if (verdict === "MAYBE") return { label: "Watchlist", className: "text-amber-500" };
  if (verdict === "NO-GO") return { label: verdictShort("NO-GO"), className: "text-destructive" };
  if (typeof score === "number") return { label: "Scored", className: "text-accent" };
  if (deal.status === "complete" || deal.status === "scored") return { label: "Awaiting verdict", className: "text-muted-foreground" };
  return { label: "In progress", className: "text-muted-foreground" };
};

/**
 * Canonical deal score/verdict resolution. Precedence (must match
 * CopilotIntelligencePanel + normalizeDealIntelligence):
 *   0. orchestrator_output.deal_score.score (authoritative, server-computed)
 *   1. orchestrator_output.score / final_decision.score (raw deal score)
 *   2. deal_versions.deal_score   (authoritative scoring engine output)
 *   3. preliminary_result.overall_score  (legacy V1 fallback)
 *
 * NEVER fall back to confidence_score — confidence is a *separate* metric
 * (data trust 0-100), not the deal's quality score.
 */
const extractDealResult = (
  deal: DealRow,
  intel?: IntelRow,
  version?: VersionRow,
): { verdict: Verdict; score: number | undefined } => {
  const orch = asObj(intel?.orchestrator_output);
  const fd = asObj(intel?.final_decision);

  const orchDecision = orch?.final_decision;
  const orchDecisionObj = asObj(orchDecision);
  const decisionRaw =
    (typeof orchDecision === "string" ? orchDecision : orchDecisionObj?.decision) ??
    fd?.decision ??
    version?.verdict;
  let verdict = normalizeVerdict(decisionRaw);

  // Deal score precedence — confidence_score is intentionally excluded.
  const dealScoreObj = asObj(orch?.deal_score);
  let score: number | undefined =
    dealScoreObj?.scored === true && typeof dealScoreObj.score === "number" ? dealScoreObj.score
    : typeof orch?.score === "number" ? orch.score
    : typeof fd?.score === "number" ? fd.score
    : typeof orchDecisionObj?.score === "number" ? orchDecisionObj.score
    : typeof version?.deal_score === "number" ? version.deal_score
    : undefined;

  // Fall back to V1 preliminary_result
  if (!verdict || score === undefined) {
    const pr = asObj(deal.preliminary_result);
    if (!verdict) verdict = normalizeVerdict(pr?.verdict);
    if (score === undefined && typeof pr?.overall_score === "number") score = pr.overall_score;
  }

  if (typeof score === "number") score = Math.max(0, Math.min(100, Math.round(score)));
  return { verdict, score };
};

const verdictBadge = (verdict?: Verdict) => {
  switch (verdict) {
    case "GO":
      return { icon: CheckCircle2, label: verdictShort("GO"), className: "text-green-500 bg-green-500/10 border-green-500/30" };
    case "NO-GO":
      return { icon: XCircle, label: verdictShort("NO-GO"), className: "text-destructive bg-destructive/10 border-destructive/30" };
    case "MAYBE":
      return { icon: AlertTriangle, label: verdictShort("MAYBE"), className: "text-amber-500 bg-amber-500/10 border-amber-500/30" };
    default:
      return { icon: Clock, label: "Draft", className: "text-muted-foreground bg-muted border-border" };
  }
};

const DealsV2MyDeals = () => {
  const navigate = useNavigate();
  const [deals, setDeals] = useState<DealRow[]>([]);
  const [intelByDeal, setIntelByDeal] = useState<Record<string, IntelRow>>({});
  const [versionByDeal, setVersionByDeal] = useState<Record<string, VersionRow>>({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterVerdict, setFilterVerdict] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [deleteTarget, setDeleteTarget] = useState<DealRow | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchIntelForDeals = async (ids: string[]): Promise<Record<string, IntelRow>> => {
    if (ids.length === 0) return {};
    const { data: intel, error } = await supabase
      .from("deal_intelligence")
      .select("deal_id, orchestrator_output, final_decision, confidence_engine, created_at, updated_at")
      .in("deal_id", ids);
    if (error) throw error;
    const map: Record<string, IntelRow> = {};
    for (const i of (intel || []) as IntelRow[]) map[i.deal_id] = i;
    return map;
  };

  const fetchVersionsForDeals = async (ids: string[]): Promise<Record<string, VersionRow>> => {
    if (ids.length === 0) return {};
    const { data: rows } = await supabase
      .from("deal_versions")
      .select("deal_id, deal_score, verdict, created_at")
      .in("deal_id", ids)
      .order("created_at", { ascending: false });
    const map: Record<string, VersionRow> = {};
    for (const r of (rows || []) as Array<VersionRow & { created_at?: string }>) {
      if (!map[r.deal_id]) map[r.deal_id] = { deal_id: r.deal_id, deal_score: r.deal_score, verdict: r.verdict };
    }
    return map;
  };


  const handleRefreshScores = async () => {
    const ids = filtered.map((d) => d.id);
    if (ids.length === 0) {
      toast({ title: "Nothing to refresh", description: "No visible deals." });
      return;
    }
    setRefreshing(true);
    try {
      const [intelMap, versionMap] = await Promise.all([
        fetchIntelForDeals(ids),
        fetchVersionsForDeals(ids),
      ]);
      setIntelByDeal((prev) => ({ ...prev, ...intelMap }));
      setVersionByDeal((prev) => ({ ...prev, ...versionMap }));
      toast({ title: "Scores refreshed", description: `Updated ${ids.length} deal${ids.length !== 1 ? "s" : ""}.` });
    } catch (e) {
      console.error(e);
      toast({ title: "Refresh failed", description: "Could not re-fetch scores.", variant: "destructive" });
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    let intelChannel: ReturnType<typeof supabase.channel> | null = null;

    const load = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate("/auth"); return; }

      const activeWsId = localStorage.getItem("brickshare_active_workspace");
      if (!activeWsId) { setDeals([]); setLoading(false); return; }

      // Superadmin workspace = read-through view across ALL workspaces.
      // Any other workspace = strict workspace scoping.
      const superadmin = await isSuperadminWorkspace(activeWsId);
      let query = supabase
        .from("deals")
        .select("*")
        .not("deal_type_id", "is", null)
        .order("updated_at", { ascending: false });
      if (!superadmin) query = query.eq("workspace_id", activeWsId);
      const { data, error } = await query;

      if (error) { console.error(error); setLoading(false); return; }
      const dealRows = (data || []) as DealRow[];
      setDeals(dealRows);

      // Fetch intelligence for these deals (RLS-scoped)
      const ids = dealRows.map((d) => d.id);
      if (ids.length > 0) {
        const [intelRes, versionMap] = await Promise.all([
          supabase
            .from("deal_intelligence")
            .select("deal_id, orchestrator_output, final_decision, confidence_engine, created_at, updated_at")
            .in("deal_id", ids),
          fetchVersionsForDeals(ids),
        ]);
        const map: Record<string, IntelRow> = {};
        for (const i of (intelRes.data || []) as IntelRow[]) map[i.deal_id] = i;
        setIntelByDeal(map);
        setVersionByDeal(versionMap);

        // Realtime: refresh a single deal's intel when it changes
        intelChannel = supabase
          .channel("my-deals-intel")
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "deal_intelligence", filter: `deal_id=in.(${ids.join(",")})` },
            (payload) => {
              const row = (payload.new ?? payload.old) as IntelRow | undefined;
              if (!row?.deal_id) return;
              setIntelByDeal((prev) => {
                if (payload.eventType === "DELETE") {
                  const next = { ...prev };
                  delete next[row.deal_id];
                  return next;
                }
                return { ...prev, [row.deal_id]: row };
              });
            }
          )
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "deals", filter: `id=in.(${ids.join(",")})` },
            (payload) => {
              const row = (payload.new ?? payload.old) as DealRow | undefined;
              if (!row?.id) return;
              setDeals((prev) => {
                if (payload.eventType === "DELETE") return prev.filter((d) => d.id !== row.id);
                const exists = prev.some((d) => d.id === row.id);
                const next = exists ? prev.map((d) => (d.id === row.id ? { ...d, ...row } : d)) : [row, ...prev];
                return next.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
              });
            }
          )
          .on(
            "postgres_changes",
            { event: "*", schema: "public", table: "deal_versions", filter: `deal_id=in.(${ids.join(",")})` },
            (payload) => {
              const row = (payload.new ?? payload.old) as { deal_id?: string; deal_score?: number | null; verdict?: string | null } | undefined;
              if (!row?.deal_id) return;
              setVersionByDeal((prev) => ({
                ...prev,
                [row.deal_id!]: { deal_id: row.deal_id!, deal_score: row.deal_score ?? null, verdict: row.verdict ?? null },
              }));
            }
          )
          .subscribe();
      }

      setLoading(false);
    };
    load();

    return () => {
      if (intelChannel) supabase.removeChannel(intelChannel);
    };
  }, [navigate]);

  const handleNewDeal = () => navigate("/deals-v2");

  const handleOpenDeal = (deal: DealRow) => {
    const isCompleted = deal.status === "complete" || deal.status === "scored";
    navigate(isCompleted ? `/deals-v2/${deal.id}/results` : `/deals-v2/${deal.id}`);
  };

  const handleDeleteDeal = async (id: string) => {
    const { error } = await supabase.from("deals").delete().eq("id", id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete deal.", variant: "destructive" });
    } else {
      setDeals((prev) => prev.filter((d) => d.id !== id));
      toast({ title: "Deal deleted" });
    }
  };

  const getDealTitle = (deal: DealRow) => {
    if (deal.name) return deal.name;
    const ctx = deal.context as Record<string, unknown>;
    const name = ctx?.deal_name as string;
    if (name) return name;
    const units = ctx?.unit_count as number;
    if (units) return `${units}-Unit Deal`;
    return "Untitled Deal";
  };

  const getDealMeta = (deal: DealRow) => {
    const ctx = deal.context as Record<string, unknown>;
    const parts: string[] = [];
    if (ctx?.occupancy_current) parts.push(`${ctx.occupancy_current}% occ`);
    if (ctx?.year_built) parts.push(`Built ${ctx.year_built}`);
    if (ctx?.hold_period) parts.push(`${ctx.hold_period} yr hold`);
    return parts.join(" · ") || "—";
  };

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

  const formatTime = (iso: string) =>
    new Date(iso).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });

  const filtered = deals
    .filter((d) => {
      if (filterVerdict !== "all") {
        const { verdict } = extractDealResult(d, intelByDeal[d.id], versionByDeal[d.id]);
        if (filterVerdict === "draft" && verdict) return false;
        if (filterVerdict !== "draft" && verdict !== filterVerdict) return false;
      }
      if (search.trim()) {
        const haystack = (getDealTitle(d) + " " + JSON.stringify(d.context)).toLowerCase();
        if (!haystack.includes(search.toLowerCase())) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "newest") return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      if (sortBy === "oldest") return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      if (sortBy === "score") {
        const sa = extractDealResult(a, intelByDeal[a.id], versionByDeal[a.id]).score ?? -1;
        const sb = extractDealResult(b, intelByDeal[b.id], versionByDeal[b.id]).score ?? -1;
        return sb - sa;
      }
      return 0;
    });

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-background flex flex-col">
      <div className="px-6 py-8 sm:py-12 max-w-5xl mx-auto w-full space-y-6">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="flex items-end justify-between gap-4">
          <div>
            <p className="font-mono text-2xs uppercase tracking-widest text-muted-foreground mb-1">Portfolio · V2</p>
            <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">My Deals</h1>
            <p className="text-sm text-muted-foreground mt-1">{deals.length} deal{deals.length !== 1 ? "s" : ""} total</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Button
              onClick={handleRefreshScores}
              disabled={refreshing}
              variant="outline"
              className="font-display h-10 px-4"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
              Refresh scores
            </Button>
            <Button onClick={handleNewDeal} className="bg-accent text-accent-foreground hover:bg-accent/90 font-display h-10 px-5">
              <Plus className="w-4 h-4 mr-2" /> New Deal
            </Button>
          </div>
        </motion.div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search deals..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10 font-mono text-sm" />
          </div>
          <Select value={filterVerdict} onValueChange={setFilterVerdict}>
            <SelectTrigger className="w-[130px] font-mono text-xs">
              <SlidersHorizontal className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Deals</SelectItem>
              <SelectItem value="GO">{verdictLong("GO")}</SelectItem>
              <SelectItem value="MAYBE">{verdictLong("MAYBE")}</SelectItem>
              <SelectItem value="NO-GO">{verdictLong("NO-GO")}</SelectItem>
              <SelectItem value="draft">Drafts</SelectItem>
            </SelectContent>
          </Select>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[130px] font-mono text-xs">
              <ArrowUpDown className="w-3.5 h-3.5 mr-1.5" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest</SelectItem>
              <SelectItem value="oldest">Oldest</SelectItem>
              <SelectItem value="score">Highest Score</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Table */}
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/30">
                  <th className="text-left p-3 micro-label text-muted-foreground">Deal</th>
                  <th className="text-center p-3 micro-label text-muted-foreground hidden sm:table-cell">Score</th>
                  <th className="text-center p-3 micro-label text-muted-foreground">Verdict</th>
                  <th className="text-left p-3 micro-label text-muted-foreground hidden md:table-cell">Details</th>
                  <th className="text-left p-3 micro-label text-muted-foreground hidden lg:table-cell">Updated</th>
                  <th className="text-center p-3 micro-label text-muted-foreground w-12"></th>
                </tr>
              </thead>
              <tbody>
                <AnimatePresence mode="popLayout">
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="text-center py-16">
                        <div className="space-y-3">
                          <Building2 className="w-10 h-10 text-muted-foreground mx-auto" />
                          <p className="font-display text-sm font-semibold">{deals.length === 0 ? "No V2 deals yet" : "No matching deals"}</p>
                          <p className="text-xs text-muted-foreground">{deals.length === 0 ? "Start analyzing your first deal." : "Try adjusting your filters."}</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filtered.map((deal, i) => {
                      const { verdict, score } = extractDealResult(deal, intelByDeal[deal.id], versionByDeal[deal.id]);
                      const milestone = getDealMilestone(deal, intelByDeal[deal.id], versionByDeal[deal.id]);
                      const badge = verdictBadge(verdict);
                      const BadgeIcon = badge.icon;

                      return (
                        <motion.tr
                          key={deal.id}
                          layout
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                          transition={{ delay: i * 0.02 }}
                          className="border-b border-border last:border-0 hover:bg-muted/20 cursor-pointer transition-colors"
                          onClick={() => handleOpenDeal(deal)}
                        >
                          <td className="p-3">
                            <p className="font-display text-sm font-semibold">{getDealTitle(deal)}</p>
                            <p className="font-mono text-[11px] text-muted-foreground">{deal.strategy.replace(/_/g, " ")}</p>
                            <p className={`micro-label mt-1 ${milestone.className}`}>{milestone.label}</p>
                          </td>
                          <td className="p-3 text-center hidden sm:table-cell">
                            {score !== undefined ? (
                              <span className={`font-display text-lg font-bold ${score >= 75 ? "text-green-500" : score >= 50 ? "text-amber-500" : "text-destructive"}`}>{score}</span>
                            ) : (
                              <span className="text-muted-foreground">—</span>
                            )}
                          </td>
                          <td className="p-3 text-center">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-2xs font-mono uppercase ${badge.className}`}>
                              <BadgeIcon className="w-2.5 h-2.5" />
                              {badge.label}
                            </span>
                          </td>
                          <td className="p-3 hidden md:table-cell">
                            <span className="font-mono text-xs text-muted-foreground">{getDealMeta(deal)}</span>
                          </td>
                          <td className="p-3 hidden lg:table-cell">
                            <p className="font-mono text-xs text-muted-foreground">{formatDate(deal.updated_at)}</p>
                            <p className="font-mono text-2xs text-muted-foreground/60">{formatTime(deal.updated_at)}</p>
                          </td>
                          <td className="p-3 text-center">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="icon" className="w-7 h-7">
                                  <MoreHorizontal className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleOpenDeal(deal); }}>
                                  <Eye className="w-3.5 h-3.5 mr-2" /> View Deal
                                </DropdownMenuItem>
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={(e) => { e.stopPropagation(); setDeleteTarget(deal); }}
                                >
                                  <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </motion.tr>
                      );
                    })
                  )}
                </AnimatePresence>
              </tbody>
            </table>
          </div>
        </div>

        {/* Delete confirmation dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete this deal?</AlertDialogTitle>
              <AlertDialogDescription>
                <span className="font-semibold text-foreground">{deleteTarget ? getDealTitle(deleteTarget) : ""}</span> will be permanently removed. This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={() => { if (deleteTarget) { handleDeleteDeal(deleteTarget.id); setDeleteTarget(null); } }}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
};

export default DealsV2MyDeals;
